<template>
  <tr class="invoice-table-row" :class="{ 'row-even': index % 2 === 1 }">
    <template v-for="col in columns">
      <!-- actions -->
      <td
        v-if="col.value === 'actions'"
        :key="'actions-' + index"
        data-col="actions"
        class="column-pin-cell"
        :class="{ 'is-sticky': isStickyColumn(col) }"
        :style="cellStickyStyle(col)"
      >
        <v-menu offset-y>
          <template v-slot:activator="{ attrs, on }">
            <v-btn color="primary" v-bind="attrs" icon v-on="on">
              <v-icon medium> mdi-dots-vertical </v-icon>
            </v-btn>
          </template>
          <v-list dense>
            <v-list-item
              v-if="showSerialFeature && rowHasSerial"
              link
              @click="$emit('open-serial-dialog', item)"
            >
              <v-list-item-icon>
                <v-icon color="primary">mdi-barcode</v-icon>
              </v-list-item-icon>
              <v-list-item-title v-text="$t('serial-management')" />
            </v-list-item>
            <v-list-item link @click="$emit('open-delete-dialog', item)">
              <v-list-item-icon>
                <v-icon color="pink"> mdi-trash-can-outline </v-icon>
              </v-list-item-icon>
              <v-list-item-title v-text="$t('remove')" />
            </v-list-item>
          </v-list>
        </v-menu>
      </td>

      <!-- row -->
      <td
        v-else-if="col.value === 'row'"
        :key="'row-' + index"
        data-col="row"
        class="column-pin-cell"
        :class="{ 'is-sticky': isStickyColumn(col) }"
        :style="cellStickyStyle(col)"
      >
        {{ rowNumber }}
      </td>

      <!-- product -->
      <!--
        bugfix //timing render invoice each row
        Edit: lightweight placeholder; parent owns ONE shared autocomplete.
        Create: full autocomplete per row (unchanged).
      -->
      <td
        v-else-if="col.value === 'product'"
        :key="'product-' + index"
        data-col="product"
        class="column-pin-cell"
        :class="{ 'is-sticky': isStickyColumn(col) }"
        :style="cellStickyStyle(col)"
      >
        <div
          v-if="id"
          class="product-autocomplete-placeholder"
          :class="{ 'is-shared-active': sharedProductActive }"
          role="button"
          tabindex="0"
          @click="requestSharedProductEdit($event, 'open')"
          @keydown.enter.prevent="requestSharedProductEdit($event, 'open')"
        >
          <template v-if="!sharedProductActive">
            <span class="product-autocomplete-placeholder__name">
              {{ productDisplayName }}
            </span>
            <span class="product-autocomplete-placeholder__icons">
              <v-progress-circular
                v-if="sharedProductLoading"
                indeterminate
                size="22"
                width="3"
                color="primary"
                class="product-autocomplete-placeholder__loader"
              />
              <template v-else>
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      color="primary"
                      v-bind="attrs"
                      icon
                      small
                      tabindex="-1"
                      class="product-autocomplete-placeholder__icon-btn"
                      v-on="on"
                      @click.stop.prevent="requestSharedProductEdit($event, 'new')"
                    >
                      <v-icon medium>mdi-plus</v-icon>
                    </v-btn>
                  </template>
                  <span>{{ $t("product") }}</span>
                </v-tooltip>
                <v-tooltip v-if="showSerialFeature && rowHasSerial" bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      color="primary"
                      v-bind="attrs"
                      icon
                      small
                      tabindex="-1"
                      class="product-autocomplete-placeholder__icon-btn"
                      v-on="on"
                      @click.stop.prevent="requestSharedProductEdit($event, 'serial')"
                    >
                      <v-icon
                        medium
                        class="product-autocomplete-placeholder__icon--serial"
                      >
                        mdi-barcode
                      </v-icon>
                    </v-btn>
                  </template>
                  <span>{{ $t("serial-management") }}</span>
                </v-tooltip>
                <!-- <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      color="primary"
                      v-bind="attrs"
                      icon
                      small
                      tabindex="-1"
                      class="product-autocomplete-placeholder__icon-btn"
                      v-on="on"
                      @click.stop.prevent="requestSharedProductEdit($event, 'barcode')"
                    >
                      <v-icon medium>mdi-barcode-scan</v-icon>
                    </v-btn>
                  </template>
                  <span>{{ $t("barcode") }}</span>
                </v-tooltip> -->
              </template>
            </span>
          </template>
        </div>
        <autocomplete
          v-else
          ref="productAutocomplete"
          v-model="item.product"
          class=""
          :rules="item.product ? [(v) => !!v || $t('is-required')] : []"
          type="productService"
          :data="JSON.stringify({ entrepot: entrepot ? entrepot.id : null })"
          :new-btn="true"
          :new-style="true"
          :value="item.product"
          :type-prop="type"
          :serial-btn="showSerialFeature && rowHasSerial"
          @clear="$emit('clear-row', item)"
          @input="$emit('change-product', item, index)"
          @serial-click="$emit('open-serial-dialog', item)"
        />
      </td>

      <!-- unit -->
      <td
        v-else-if="col.value === 'unit'"
        :key="'unit-' + index"
        data-col="unit"
        class="column-pin-cell"
        :class="{ 'is-sticky': isStickyColumn(col) }"
        :style="cellStickyStyle(col)"
      >
        <v-select
          v-model="item.unit"
          :items="unitItems(item)"
          height="26"
          dense
          hide-details
          class="mb-3 input-new-style"
          outlined
          return-object
          item-value="id"
          item-text="fame"
        />
      </td>

      <!-- count -->
      <td
        v-else-if="col.value === 'count'"
        :key="'count-' + index"
        data-col="count"
        class="column-pin-cell"
        :class="{ 'is-sticky': isStickyColumn(col) }"
        :style="cellStickyStyle(col)"
      >
        <div class="d-flex">
          <v-menu open-on-hover top offset-y>
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                v-model="item.count"
                v-disabled-icon-focus
                v-bind="attrs"
                height="20"
                dense
                solo
                hide-details
                flat
                class="mb-3 dense input-new-style"
                :rules="item.product ? [(v) => !!v || $t('is-required')] : []"
                :background-color="$vuetify.theme.isDark ? '#1E1E1E' : '#e5e5e5'"
                :placeholder="$t('count')"
                type="number"
                v-on="on"
                @blur="$emit('zero-count-check', item)"
                @input="$emit('check-count', item)"
                @focus="focusEvent"
              />
            </template>
            <v-list dense v-if="item.product">
              <v-list-item>
                <v-list-item-title
                  >{{ $t("entrepotStock") }} :
                  {{
                    item.product && item.product.stock ? item.product.stock.count : 0
                  }}</v-list-item-title
                >
              </v-list-item>
              <v-list-item>
                <v-list-item-title
                  >{{ $t("reservedCount") }} :
                  {{
                    item.product && item.product.reserved
                      ? item.product.reserved.stock + ""
                      : 0
                  }}</v-list-item-title
                >
              </v-list-item>
              <v-list-item>
                <v-list-item-title
                  >{{ $t("remain") }} :
                  {{ item.product ? item.product.remain : 0 }}</v-list-item-title
                >
              </v-list-item>
            </v-list>
          </v-menu>
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                v-show="item.product && item.product.kind_id === 112"
                v-bind="attrs"
                small
                outlined
                color="primary"
                style="margin-right: 2px !important; margin-top: 1px"
                v-on="on"
                @click="$emit('open-gold-dialog', item)"
              >
                <v-icon style="font-size: 22px"> mdi-gold </v-icon>
              </v-btn>
            </template>
            <span>{{ $t("goldCalculator") }}</span>
          </v-tooltip>
          <p
            v-if="item.product && unitItems(item).length > 1 && unitCalc(item) !== 1"
            style="min-width: 80px"
            class="px-2 px-2 pt-2 my-0"
          >
            = {{ item.count * unitCalc(item) }} {{ unitItems(item)[0].fame }}
          </p>
        </div>
      </td>

      <!-- fee -->
      <td
        v-else-if="col.value === 'fee'"
        :key="'fee-' + index"
        data-col="fee"
        class="column-pin-cell"
        :class="{ 'is-sticky': isStickyColumn(col) }"
        :style="cellStickyStyle(col)"
      >
        <money
          v-model="item.fee"
          :dense="true"
          :outlined="true"
          :rules="item.product ? [feeValidate] : []"
          :solo="false"
          :new-style="true"
          :clearable="true"
          value-when-is-empty=""
          :options="options"
          :disabled="sellPriceLocked"
          class="mb-3 dense"
          @input="$emit('calc-sum')"
        />
      </td>

      <!-- discount -->
      <td
        v-else-if="col.value === 'discount'"
        :key="'discount-' + index"
        data-col="discount"
        class="column-pin-cell"
        :class="{ 'is-sticky': isStickyColumn(col) }"
        :style="cellStickyStyle(col)"
      >
        <div class="d-flex">
          <money
            v-model="item.discount"
            :dense="true"
            :outlined="true"
            :solo="false"
            :clearable="true"
            :new-style="true"
            value-when-is-empty=""
            :options="options"
            class="mb-3 dense"
            :disabled="sellPriceLocked ? true : item.fee * item.count > 0 ? false : true"
            @input="$emit('check-discount', item)"
          />
          <v-text-field
            v-model="item.discountPercent"
            v-disabled-icon-focus
            height="22"
            dense
            flat
            outlined
            solo
            suffix="%"
            class="mb-2 dense input-new-style inputPercent"
            style="max-width: 86px"
            type="number"
            :disabled="sellPriceLocked"
            @focus="focusEvent"
            @input="$emit('check-discount-percent', item)"
          />
        </div>
      </td>

      <!-- tax -->
      <td
        v-else-if="col.value === 'tax'"
        :key="'tax-' + index"
        data-col="tax"
        class="column-pin-cell"
        :class="{ 'is-sticky': isStickyColumn(col) }"
        :style="cellStickyStyle(col)"
      >
        <div class="d-flex">
          <money
            v-model="item.tax"
            :dense="true"
            :outlined="true"
            :solo="false"
            :new-style="true"
            value-when-is-empty=""
            :options="options"
            class="mb-3 dense"
            :disabled="true"
          />
          <v-text-field
            v-if="item.product"
            v-model="item.product.tax_percent"
            v-disabled-icon-focus
            height="20"
            dense
            flat
            outlined
            solo
            suffix="%"
            class="mb-2 dense input-new-style inputPercent"
            style="max-width: 86px"
            type="number"
            @focus="focusEvent"
            @input="$emit('calc-sum')"
          />
        </div>
      </td>

      <!-- sum -->
      <td
        v-else-if="col.value === 'sum'"
        :key="'sum-' + index"
        data-col="sum"
        class="column-pin-cell"
        :class="{ 'is-sticky': isStickyColumn(col) }"
        :style="cellStickyStyle(col)"
      >
        {{
          $numberFormat(
            (
              parseFloat(
                item.fee *
                  item.count *
                  (item.cutie && item.cutie !== 0 && item.cutie !== "0"
                    ? item.cutie / 750
                    : 1
                  ).toFixed(2)
              ) +
              parseFloat(item.tax || 0) -
              parseFloat(item.discount || 0) +
              parseFloat(item.construction_wages || 0) +
              parseFloat(item.profit || 0) +
              parseFloat(item.commission || 0)
            ).toFixed(decimal)
          )
        }}
      </td>

      <!-- content -->
      <td
        v-else-if="col.value === 'content'"
        :key="'content-' + index"
        data-col="content"
        class="column-pin-cell"
        :class="{ 'is-sticky': isStickyColumn(col) }"
        :style="cellStickyStyle(col)"
      >
        <v-text-field
          v-model="item.content"
          height="20"
          dense
          solo
          flat
          class="mb-3 dense input-new-style"
          :background-color="$vuetify.theme.isDark ? '#1E1E1E' : '#e5e5e5'"
          clearable
          :placeholder="$t('content')"
        />
      </td>

      <!-- entrepot -->
      <td
        v-else-if="col.value === 'entrepot'"
        :key="'entrepot-' + index"
        data-col="entrepot"
        class="column-pin-cell"
        :class="{ 'is-sticky': isStickyColumn(col) }"
        :style="cellStickyStyle(col)"
      >
        <autocomplete
          v-model="item.article_entrepot"
          class="mb-3"
          :new-style="true"
          type="entrepot"
          :getInitItemsOff="true"
          @input="$emit('change-entrepot', item, index)"
        />
      </td>

      <!-- expire_date -->
      <td
        v-else-if="col.value === 'expire_date'"
        :key="'expire_date-' + index"
        data-col="expire_date"
        class="column-pin-cell"
        :class="{ 'is-sticky': isStickyColumn(col) }"
        :style="cellStickyStyle(col)"
      >
        <v-text-field
          :id="'expireDate' + index"
          v-model="item.expire_date"
          clear-icon="flaticon2-close-cross"
          outlined
          dense
          height="25"
          class="mb-3"
          clearable
        />
        <date-picker
          v-model="item.expire_date"
          :element="'expireDate' + index"
          :locale="$store.state.date_locale"
          :format="$store.state.date_format"
          auto-submit
          :color="$vuetify.theme.themes[$vuetify.theme.isDark ? 'dark' : 'light'].primary"
        />
      </td>
    </template>
  </tr>
</template>

<script>
import autocomplete from "../../../components/general/patial/autocomplete";
import Money from "../../../components/general/Money/index";
import { getStickyStyleForColumn } from "@/plugins/columnPin/columnPin";

export default {
  name: "InvoiceTableRow",
  components: {
    autocomplete,
    Money,
  },
  directives: {
    disabledIconFocus: {
      bind(el) {
        el.querySelectorAll(".v-input__icon button").forEach((x) =>
          x.setAttribute("tabindex", -1)
        );
      },
    },
  },
  props: {
    item: {
      type: Object,
      required: true,
    },
    index: {
      type: Number,
      required: true,
    },
    rowNumber: {
      type: [Number, String],
      required: true,
    },
    type: {
      type: String,
      required: true,
    },
    id: {
      default: null,
    },
    entrepot: {
      default: null,
    },
    options: {
      type: Object,
      required: true,
    },
    decimal: {
      type: [Number, String],
      default: 0,
    },
    showSerialFeature: {
      type: Boolean,
      default: false,
    },
    columns: {
      type: Array,
      default: function () {
        return [];
      },
    },
    lockedColumns: {
      type: Array,
      default: function () {
        return ["actions", "row", "product"];
      },
    },
    // bugfix //timing render invoice each row — parent shared product editor
    sharedProductActive: {
      type: Boolean,
      default: false,
    },
    sharedProductLoading: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    productDisplayName() {
      const product = this.item && this.item.product;
      if (!product) return "";
      return product.name || product.fame || product.title || "";
    },
    sellPriceLocked() {
      return (
        (this.type == "sell" || this.type == "presale") &&
        this.$store.state.allConfig &&
        this.$store.state.allConfig.userInt &&
        this.$store.state.allConfig.userInt.deactiveChangeSellPrice == "active"
      );
    },
    rowHasSerial() {
      return !!(
        this.item &&
        (this.item.has_serial === "active" || this.getProductHasSerial(this.item.product))
      );
    },
    isRtl() {
      return !!(this.$vuetify && this.$vuetify.rtl);
    },
  },
  methods: {
    requestSharedProductEdit(e, action) {
      if (!this.id || this.sharedProductActive) return;
      const nextAction = action || "open";
      // Serial does not need the shared autocomplete
      if (nextAction === "serial") {
        this.$emit("open-serial-dialog", this.item);
        return;
      }
      const el =
        (e &&
          e.target &&
          e.target.closest &&
          e.target.closest(".product-autocomplete-placeholder")) ||
        (e && e.currentTarget) ||
        null;
      this.$emit("activate-shared-product", {
        item: this.item,
        index: this.index,
        el,
        action: nextAction,
      });
    },
    isStickyColumn(col) {
      if (!col) return false;
      return this.lockedColumns.indexOf(col.value) !== -1 || !!col.pinned;
    },
    cellStickyStyle(col) {
      if (!col || !this.isStickyColumn(col)) return null;
      return getStickyStyleForColumn(
        this.columns,
        col.value,
        this.lockedColumns,
        this.isRtl
      );
    },
    clear() {
      const ref = this.$refs.productAutocomplete;
      const autocompleteRef = Array.isArray(ref) ? ref[0] : ref;
      if (autocompleteRef && autocompleteRef.clear) {
        autocompleteRef.clear();
      }
    },
    getProductHasSerial(product) {
      if (!product) return false;
      const flag = (product.meta && product.meta.has_serial) || product.has_serial;
      return flag === "active";
    },
    unitCalc(item) {
      if (item.product && item.unit) {
        var i = item.product.units.findIndex((x) => x.unit_id === item.unit.id);
        if (i !== -1 && item.product.units[i].value) {
          return +item.product.units[i].value;
        } else {
          return 1;
        }
      }
    },
    unitItems(item) {
      if (item.product) {
        var units = [item.product.unit];
        if (item.product.units && item.product.units.length > 0) {
          item.product.units.map(function (value) {
            value.unit.value = value.value;
            units.push(value.unit);
          });
        }
        if (!item.unit) {
          item.unit = item.product.unit;
        }
        return units;
      } else {
        return [];
      }
    },
  },
};
</script>

<style scoped>
/* bugfix //timing render invoice each row — fake product cell (same look as autocomplete) */
.product-autocomplete-placeholder {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 4px;
  min-height: 32px;
  height: 32px;
  padding: 0 4px 0 0;
  background: transparent;
  border: none;
  box-shadow: none;
  cursor: pointer;
  width: 100%;
  box-sizing: border-box;
  border-radius: 3px;
  transition: background-color 0.12s ease;
}
.product-autocomplete-placeholder.is-shared-active {
  cursor: default;
  min-height: 32px;
}
.product-autocomplete-placeholder__loader {
  margin: 0 8px;
  flex: 0 0 auto;
}
.product-autocomplete-placeholder__name {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 12px;
  line-height: 32px;
  color: rgba(0, 0, 0, 0.87);
}
.product-autocomplete-placeholder__icons {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  flex: 0 0 auto;
  margin-top: 0;
  padding-left: 1px;
}
.product-autocomplete-placeholder__icon-btn {
  width: 28px !important;
  height: 28px !important;
  margin: 0 !important;
  background: transparent !important;
}
.product-autocomplete-placeholder__icon-btn:hover,
.product-autocomplete-placeholder__icon-btn:focus,
.product-autocomplete-placeholder__icon-btn::before {
  background: transparent !important;
}
.product-autocomplete-placeholder__icon-btn .v-icon {
  font-size: 18px !important;
  width: 18px !important;
  height: 18px !important;
}
.product-autocomplete-placeholder__icon--serial {
  transform: rotate(90deg);
  transform-origin: center center;
}
.invoice-table-row {
  height: 30px !important;
}
.invoice-table-row.row-even {
  background: #fff;
}
.invoice-table-row:not(.row-even) {
  background: #f5f5f5;
}
.invoice-table-row >>> td {
  padding: 4px 4px !important;
  vertical-align: middle !important;
  font-size: 12px !important;
}
.invoice-table-row.row-even >>> td {
  background: #fff !important;
}
.invoice-table-row:not(.row-even) >>> td {
  background: #f5f5f5 !important;
}
.invoice-table-row >>> td.is-sticky {
  position: sticky;
  border-top: none !important;
  overflow: hidden !important;
}
.invoice-table-row.row-even >>> td.is-sticky {
  background: #fff !important;
}
.invoice-table-row:not(.row-even) >>> td.is-sticky {
  background: #f5f5f5 !important;
}
.invoice-table-row >>> td[data-col="product"].is-sticky {
  border-left: 1px solid #e0e0e0;
}
.invoice-table-row >>> .v-input,
.invoice-table-row >>> .v-text-field,
.invoice-table-row >>> .v-select {
  margin-top: 0 !important;
  margin-bottom: 0 !important;
  align-self: center;
}
.invoice-table-row >>> td > .v-input,
.invoice-table-row >>> td > .d-flex {
  display: flex !important;
  align-items: center !important;
  width: 100%;
  gap: 0;
}
.invoice-table-row >>> td > .d-flex > div {
  flex: 1 1 auto;
  min-width: 0;
}
.invoice-table-row >>> .v-btn.v-btn--icon {
  width: 28px !important;
  height: 28px !important;
}
.invoice-table-row >>> .v-btn.v-btn--icon .v-icon {
  font-size: 18px !important;
}
.invoice-table-row >>> .v-input {
  font-size: 12px !important;
  margin-top: 0 !important;
  margin-bottom: 0 !important;
}
.invoice-table-row >>> .mb-3,
.invoice-table-row >>> .mb-2 {
  margin-bottom: 0 !important;
}
.invoice-table-row >>> .v-text-field,
.invoice-table-row >>> .v-select {
  margin-top: 0 !important;
  padding-top: 0 !important;
}
.invoice-table-row >>> .v-text-field--outlined .v-input__control,
.invoice-table-row >>> .v-text-field--outlined .v-input__slot,
.invoice-table-row >>> .v-select.v-text-field--outlined .v-input__control,
.invoice-table-row >>> .v-select.v-text-field--outlined .v-input__slot,
.invoice-table-row >>> .v-text-field--solo .v-input__control,
.invoice-table-row >>> .v-text-field--solo .v-input__slot {
  min-height: 26px !important;
  height: 26px !important;
}
.invoice-table-row >>> .v-select.v-text-field--outlined .v-input__slot {
  padding-left: 0 !important;
  padding-right: 0 !important;
}
.invoice-table-row >>> td[data-col="count"] {
  padding-left: 2px !important;
  padding-right: 2px !important;
}
.invoice-table-row
  >>> .v-text-field--outlined.v-input--dense
  > .v-input__control
  > .v-input__slot,
.invoice-table-row >>> .v-select.v-input--dense > .v-input__control > .v-input__slot,
.invoice-table-row
  >>> .v-text-field--solo.v-input--dense
  > .v-input__control
  > .v-input__slot {
  min-height: 26px !important;
  height: 26px !important;
}
.invoice-table-row >>> .v-text-field--outlined fieldset {
  top: -3px;
  border-radius: 3px;
}
.invoice-table-row >>> input,
.invoice-table-row >>> .v-select__selection,
.invoice-table-row >>> .v-select__selections {
  font-size: 12px !important;
  line-height: 18px !important;
  padding-top: 0 !important;
  padding-bottom: 0 !important;
  max-height: 26px;
  margin: 0 !important;
}
.invoice-table-row >>> .v-label {
  font-size: 11px !important;
}
.invoice-table-row >>> .v-input__append-inner,
.invoice-table-row >>> .v-input__prepend-inner,
.invoice-table-row >>> .v-input__append-outer,
.invoice-table-row >>> .v-input__prepend-outer {
  display: flex !important;
  align-items: center !important;
  align-self: center !important;
  height: 26px !important;
  margin-top: 0 !important;
  margin-bottom: 0 !important;
  padding-top: 0 !important;
  padding-bottom: 0 !important;
}
.invoice-table-row >>> .v-input__append-outer {
  margin: 0 !important;
  padding: 0 !important;
  width: 0 !important;
  min-width: 0 !important;
  overflow: hidden !important;
}
.invoice-table-row >>> .v-input__append-inner {
  margin: 0 !important;
  padding: 0 !important;
  min-width: 0 !important;
}
.invoice-table-row >>> .inputPercent {
  margin-inline-start: 2px !important;
  flex: 0 0 86px !important;
  max-width: 86px !important;
}
.invoice-table-row >>> .inputPercent .v-input__slot {
  padding-left: 4px !important;
  padding-right: 4px !important;
}
.invoice-table-row >>> .inputPercent .v-text-field__suffix {
  padding-left: 2px !important;
  padding-right: 0 !important;
  margin: 0 !important;
}
.invoice-table-row >>> .v-input__append-inner .v-btn,
.invoice-table-row >>> .v-input__prepend-inner .v-btn,
.invoice-table-row >>> .v-input__append-outer .v-btn,
.invoice-table-row >>> .v-input__prepend-outer .v-btn,
.invoice-table-row >>> .v-input__append-inner .v-icon,
.invoice-table-row >>> .v-input__prepend-inner .v-icon,
.invoice-table-row >>> .v-input__append-outer .v-icon,
.invoice-table-row >>> .v-input__prepend-outer .v-icon {
  margin-top: 0 !important;
  margin-bottom: 0 !important;
  align-self: center !important;
}
.invoice-table-row >>> .v-btn[style*="-3px"] {
  position: relative;
  top: 3px;
}
.invoice-table-row >>> .v-icon {
  font-size: 15px !important;
  height: 15px !important;
  width: 15px !important;
}
.invoice-table-row >>> .v-text-field__details,
.invoice-table-row >>> .v-messages {
  display: none !important;
  height: 0 !important;
  min-height: 0 !important;
  margin: 0 !important;
  padding: 0 !important;
  overflow: hidden !important;
}
.invoice-table-row >>> .v-input__slot {
  box-shadow: none !important;
  margin-bottom: 0 !important;
  margin-top: 0 !important;
}
.invoice-table-row >>> .v-input__control {
  min-height: 26px !important;
}
.invoice-table-row >>> .v-input--dense .v-input__slot,
.invoice-table-row >>> .v-text-field.v-text-field--enclosed .v-input__slot {
  margin-bottom: 0 !important;
}
.invoice-table-row >>> .v-input,
.invoice-table-row >>> .v-input > .v-input__control {
  align-self: center !important;
}
.input-new-style ::v-deep .v-input__slot {
  background: transparent !important;
  box-shadow: none !important;
}
.inputPercent.input-new-style ::v-deep .v-input__slot {
  border: 1px #d1d1d1 solid !important;
  box-shadow: none !important;
}
.invoice-table-row >>> .inputPercent.v-text-field--solo .v-input__control,
.invoice-table-row >>> .inputPercent.v-text-field--solo .v-input__slot,
.invoice-table-row >>> .inputPercent.v-text-field--outlined .v-input__control,
.invoice-table-row >>> .inputPercent.v-text-field--outlined .v-input__slot,
.invoice-table-row
  >>> .inputPercent.v-text-field--solo.v-input--dense
  > .v-input__control
  > .v-input__slot,
.invoice-table-row
  >>> .inputPercent.v-text-field--outlined.v-input--dense
  > .v-input__control
  > .v-input__slot {
  min-height: 22px !important;
  height: 22px !important;
}
.invoice-table-row >>> .inputPercent input {
  max-height: 22px !important;
  padding: 0 !important;
}
.input-new-style ::v-deep fieldset {
  border: none !important;
}
.input-new-style ::v-deep .v-input__slot::before,
.input-new-style ::v-deep .v-input__slot::after {
  display: none !important;
}

/* Validation: soft red cell background only (no red border) */
.invoice-table-row >>> td:has(.error--text) {
  background-color: rgba(244, 67, 54, 0.18) !important;
}
</style>
